import { useState, useRef, useCallback, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, Trash2, Type, Image as ImageIcon, List, ExternalLink, Plus, Music } from 'lucide-react';

const storage = getStorage();

const NOTE_COLORS = [
  { bg: '#0d0d1f', border: 'rgba(168,110,255,.35)', accent: '#a87ee8', label: 'purple' },
  { bg: '#0a1a12', border: 'rgba(26,144,128,.35)',  accent: '#25b8a4', label: 'teal'   },
  { bg: '#1a0d0d', border: 'rgba(232,98,26,.35)',   accent: '#f0844a', label: 'orange' },
  { bg: '#1a160a', border: 'rgba(201,138,24,.35)',  accent: '#c98a18', label: 'amber'  },
  { bg: '#0a1020', border: 'rgba(74,158,237,.35)',  accent: '#4a9eed', label: 'blue'   },
];

// ── Single draggable item ────────────────────────────────────
function CanvasItem({ item, selected, onSelect, onUpdate, onDelete }) {
  const [editingText, setEditingText] = useState(false);
  const [localText, setLocalText] = useState('');

  const startDrag = (e) => {
    if (e.target.closest('.no-drag')) return;
    e.stopPropagation();
    onSelect(item.id);
    const sx = e.clientX, sy = e.clientY, ox = item.x, oy = item.y;
    const move = (e) => onUpdate(item.id, { x: Math.max(0, ox + e.clientX - sx), y: Math.max(0, oy + e.clientY - sy) });
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const startResize = (e) => {
    e.stopPropagation();
    const sx = e.clientX, sy = e.clientY, ow = item.w, oh = item.h;
    const move = (e) => onUpdate(item.id, { w: Math.max(80, ow + e.clientX - sx), h: Math.max(40, oh + e.clientY - sy) });
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const startEdit = () => {
    setLocalText(item.text || item.caption || '');
    setEditingText(true);
  };

  const commitText = () => {
    setEditingText(false);
    if (item.type === 'image') onUpdate(item.id, { caption: localText });
    else onUpdate(item.id, { text: localText });
  };

  const col = NOTE_COLORS[item.colorIdx || 0];
  const isSel = selected;

  return (
    <div
      onMouseDown={startDrag}
      onClick={e => { e.stopPropagation(); onSelect(item.id); }}
      style={{
        position: 'absolute', left: item.x, top: item.y, width: item.w, height: item.h,
        cursor: 'grab', userSelect: 'none', zIndex: isSel ? 100 : 1,
        borderRadius: 10, overflow: 'visible',
        filter: isSel ? 'drop-shadow(0 0 8px rgba(168,110,255,.4))' : 'none',
        transition: 'filter .15s',
      }}
    >

      {/* ── IMAGE ── */}
      {item.type === 'image' && (
        <div style={{ width: '100%', height: '100%', borderRadius: 8, overflow: 'hidden', border: `2px solid ${isSel ? 'var(--border-active)' : 'transparent'}`, background: 'var(--surface3)', position: 'relative' }}>
          <img src={item.url} alt={item.caption || ''} draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onError={e => { e.currentTarget.style.opacity = '.1'; }} />
          {editingText ? (
            <input autoFocus value={localText} onChange={e => setLocalText(e.target.value)}
              onBlur={commitText} onKeyDown={e => e.key === 'Enter' && commitText()}
              className="no-drag"
              style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,.9)', border: 'none', color: '#fff', fontSize: 11, padding: '4px 8px', outline: 'none', fontFamily: 'var(--mono)' }} />
          ) : (
            <div onDoubleClick={startEdit}
              style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '3px 8px', background: 'rgba(0,0,0,.55)', fontSize: 10, color: 'rgba(255,255,255,.6)', fontFamily: 'var(--mono)', borderRadius: '0 0 6px 6px', minHeight: 20 }}>
              {item.caption || (isSel ? <span style={{ opacity: .3 }}>dbl-click to caption</span> : '')}
            </div>
          )}
        </div>
      )}

      {/* ── NOTE ── */}
      {item.type === 'note' && (
        <div style={{ width: '100%', height: '100%', background: col.bg, border: `1.5px solid ${isSel ? 'var(--border-active)' : col.border}`, borderRadius: 8, padding: '10px 12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {item.label && <div style={{ fontSize: 9, fontWeight: 700, color: col.accent, letterSpacing: 1.5, textTransform: 'uppercase', fontFamily: 'var(--mono)', marginBottom: 6, flexShrink: 0 }}>{item.label}</div>}
          {editingText ? (
            <textarea autoFocus value={localText} onChange={e => setLocalText(e.target.value)}
              onBlur={commitText} className="no-drag"
              style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text)', fontSize: 12, lineHeight: 1.7, resize: 'none', outline: 'none', fontFamily: 'var(--font)' }} />
          ) : (
            <div onDoubleClick={startEdit} style={{ flex: 1, fontSize: 12, color: 'var(--muted)', lineHeight: 1.7, whiteSpace: 'pre-wrap', overflow: 'hidden', cursor: 'text' }}>
              {item.text || <span style={{ color: 'var(--dim)', fontStyle: 'italic', fontSize: 11 }}>Double-click to edit</span>}
            </div>
          )}
        </div>
      )}

      {/* ── STEP ── */}
      {item.type === 'step' && (
        <div style={{ width: '100%', height: '100%', background: '#0a0f1a', border: `1.5px solid ${isSel ? 'var(--border-active)' : 'rgba(74,158,237,.25)'}`, borderRadius: 8, padding: '8px 12px', overflow: 'hidden', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(74,158,237,.15)', border: '1px solid rgba(74,158,237,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontFamily: 'var(--mono)', color: '#4a9eed', flexShrink: 0, marginTop: 1 }}>
            {item.stepNum || '·'}
          </div>
          {editingText ? (
            <textarea autoFocus value={localText} onChange={e => setLocalText(e.target.value)}
              onBlur={commitText} className="no-drag"
              style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text)', fontSize: 12, lineHeight: 1.6, resize: 'none', outline: 'none', fontFamily: 'var(--font)', height: '100%' }} />
          ) : (
            <div onDoubleClick={startEdit} style={{ flex: 1, fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, whiteSpace: 'pre-wrap', overflow: 'hidden', cursor: 'text' }}>
              {item.text || <span style={{ color: 'var(--dim)', fontStyle: 'italic', fontSize: 11 }}>Double-click to edit</span>}
            </div>
          )}
        </div>
      )}


      {/* ── AUDIO ── */}
      {item.type === 'audio' && (
        <div style={{ width: '100%', height: '100%', background: '#0a0f1a', border: `1.5px solid ${isSel ? 'var(--border-active)' : 'rgba(168,110,255,.25)'}`, borderRadius: 8, padding: '10px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Music size={14} style={{ color: 'var(--purple2)', flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label || 'Audio'}</span>
          </div>
          <audio controls src={item.url} style={{ width: '100%', height: 28, accentColor: 'var(--purple2)' }} onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* ── LINK ── */}
      {item.type === 'link' && (
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="no-drag"
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: '100%', background: 'rgba(26,144,128,.08)', border: `1.5px solid ${isSel ? 'var(--border-active)' : 'rgba(26,144,128,.25)'}`, borderRadius: 8, padding: '6px 12px', textDecoration: 'none' }}>
          <ExternalLink size={13} style={{ color: 'var(--teal2)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: 'var(--teal2)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label || item.url}</span>
        </a>
      )}

      {/* Controls when selected */}
      {isSel && (
        <div className="no-drag" style={{ position: 'absolute', top: -32, left: 0, display: 'flex', gap: 4, alignItems: 'center', zIndex: 200 }}>
          {(item.type === 'note' || item.type === 'step') && NOTE_COLORS.map((c, i) => (
            <button key={i} onClick={() => onUpdate(item.id, { colorIdx: i })}
              style={{ width: 13, height: 13, borderRadius: '50%', background: c.bg, border: `2px solid ${i === (item.colorIdx || 0) ? '#fff' : c.border}`, cursor: 'pointer', padding: 0 }} />
          ))}
          <button onClick={() => onDelete(item.id)} className="no-drag"
            style={{ marginLeft: 2, background: 'rgba(192,57,43,.85)', border: 'none', borderRadius: 5, padding: '2px 5px', cursor: 'pointer', color: '#fff', display: 'flex' }}>
            <Trash2 size={11} />
          </button>
        </div>
      )}

      {/* Resize */}
      {isSel && (
        <div className="no-drag" onMouseDown={startResize}
          style={{ position: 'absolute', bottom: -6, right: -6, width: 14, height: 14, background: 'var(--border-active)', borderRadius: '50%', cursor: 'se-resize', zIndex: 200 }} />
      )}
    </div>
  );
}

// ── Main MoodBoard ───────────────────────────────────────────
export default function MoodBoard({ items = [], onChange, readOnly = false, notes, links, steps, showSteps = true, fillPage = false, minHeight = null }) {
  const canvasRef = useRef();
  const fileInputRef = useRef();
  const audioInputRef = useRef();
  const seeded = useRef(false);
  const [selected, setSelected] = useState(null);
  const computedMinH = minHeight || (fillPage ? window.innerHeight - 300 : 500);
  const [canvasH, setCanvasH] = useState(minHeight || (fillPage ? 'calc(100vh - 300px)' : 500));
  const [canvasW, setCanvasW] = useState('100%');
  const [draggingOver, setDraggingOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const resizingH = useRef(false);
  const resizingW = useRef(false);
  const resizeStartY = useRef(0);
  const resizeStartH = useRef(0);
  const resizeStartX = useRef(0);
  const resizeStartW = useRef(0);

  // Resize handlers
  const onBottomMouseDown = (e) => {
    e.preventDefault();
    resizingH.current = true;
    resizeStartY.current = e.clientY;
    resizeStartH.current = typeof canvasH === 'number' ? canvasH : canvasRef.current?.offsetHeight || 500;
    const onMove = (ev) => {
      if (!resizingH.current) return;
      const delta = ev.clientY - resizeStartY.current;
      setCanvasH(Math.max(200, resizeStartH.current + delta));
    };
    const onUp = () => { resizingH.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onRightMouseDown = (e) => {
    e.preventDefault();
    resizingW.current = true;
    resizeStartX.current = e.clientX;
    resizeStartW.current = canvasRef.current?.offsetWidth || 800;
    const onMove = (ev) => {
      if (!resizingW.current) return;
      const delta = ev.clientX - resizeStartX.current;
      setCanvasW(Math.max(300, resizeStartW.current + delta));
    };
    const onUp = () => { resizingW.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Auto-expand canvas
  useEffect(() => {
    if (!items.length) return;
    const maxY = Math.max(...items.map(i => (i.y || 0) + (i.h || 0) + 80));
    setCanvasH(Math.max(computedMinH, maxY));
  }, [items]);

  // Seed existing protocol content onto canvas (once, only if empty)
  useEffect(() => {
    if (items.length > 0 || seeded.current) return;
    seeded.current = true;
    const seeds = [];
    let x = 20, y = 20;

    if (notes?.trim()) {
      seeds.push({ id: `seed-notes-${Date.now()}`, type: 'note', x, y, w: 340, h: Math.min(280, Math.max(100, notes.length / 2.5)), text: notes, label: 'NOTES', colorIdx: 1 });
      x += 360;
    }
    if (links?.length) {
      links.forEach((link, i) => {
        seeds.push({ id: `seed-link-${i}-${Date.now()}`, type: 'link', x: 20 + i * 230, y: y + (notes ? Math.min(300, Math.max(120, notes.length / 2.5)) + 20 : 0), w: 210, h: 42, url: link.url, label: link.label });
      });
    }
    if (steps?.length) {
      steps.forEach((step, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        seeds.push({ id: `seed-step-${i}-${Date.now()}`, type: 'step', x: 20 + col * 240, y: 20 + row * 110, w: 220, h: 90, text: step.text, stepNum: i + 1, colorIdx: 4 });
      });
    }
    if (seeds.length) onChange(seeds);
  }, []);

  const updateItem = useCallback((id, updates) => {
    onChange((items || []).map(i => i.id === id ? { ...i, ...updates } : i));
  }, [onChange, items]);

  const deleteItem = useCallback((id) => {
    onChange((items || []).filter(i => i.id !== id));
    setSelected(null);
  }, [onChange, items]);

  const addNote = useCallback(() => {
    const id = Date.now().toString();
    const n = (items || []).length;
    onChange([...(items || []), { id, type: 'note', x: 20 + (n % 6) * 20, y: 20 + (n % 6) * 20, w: 220, h: 160, text: '', colorIdx: n % NOTE_COLORS.length }]);
    setSelected(id);
  }, [onChange, items]);

  const addStep = useCallback(() => {
    const id = Date.now().toString();
    const steps = (items || []).filter(i => i.type === 'step');
    onChange([...(items || []), { id, type: 'step', x: 20, y: 20, w: 260, h: 80, text: '', stepNum: steps.length + 1, colorIdx: 4 }]);
    setSelected(id);
  }, [onChange, items]);

  const addImageToCanvas = (url, x = 20, y = 20) => {
    const id = Date.now().toString();
    onChange([...(items || []), { id, type: 'image', x, y, w: 220, h: 220, url, caption: '' }]);
  };

  const addAudioToCanvas = (url, label, x = 20, y = 20) => {
    const id = Date.now().toString();
    onChange([...(items || []), { id, type: 'audio', x, y, w: 280, h: 80, url, label }]);
  };

  const handleAudioInput = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let ox = 20;
    for (const file of files) {
      try {
        const storageRef = ref(storage, `protocols/audio/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        addAudioToCanvas(url, file.name, ox, 20);
        ox += 300;
      } catch (err) { console.error('Audio upload error:', err); }
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDraggingOver(false);
    if (readOnly) return;
    const canvas = canvasRef.current?.getBoundingClientRect();
    const dropX = Math.max(0, e.clientX - (canvas?.left || 0) - 110);
    const dropY = Math.max(0, e.clientY - (canvas?.top || 0) - 110);
    const dataItems = Array.from(e.dataTransfer.items || []);
    const files = Array.from(e.dataTransfer.files || []);

    const htmlItem = dataItems.find(i => i.kind === 'string' && i.type === 'text/html');
    const urlItem = dataItems.find(i => i.kind === 'string' && i.type === 'text/uri-list');

    if (htmlItem) {
      htmlItem.getAsString(html => {
        const m = html.match(/src="([^"]+)"/);
        if (m?.[1]?.startsWith('http')) addImageToCanvas(m[1], dropX, dropY);
      });
      return;
    }
    if (urlItem) {
      urlItem.getAsString(url => {
        const u = url.trim().split('\n')[0];
        if (u?.startsWith('http')) addImageToCanvas(u, dropX, dropY);
      });
      return;
    }

    const audioFiles = files.filter(f => f.type.startsWith('audio/'));
    const imageFiles = files.filter(f => f.type.startsWith('image/'));

    if (audioFiles.length) {
      setUploading(true);
      let ox = dropX;
      for (const file of audioFiles) {
        try {
          const storageRef = ref(storage, `protocols/audio/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          addAudioToCanvas(url, file.name, ox, dropY);
          ox += 300;
        } catch (err) { console.error('Audio drop error:', err); }
      }
      setUploading(false);
      if (!imageFiles.length) return;
    }
    if (imageFiles.length) {
      setUploading(true);
      let ox = dropX;
      for (const file of imageFiles) {
        try {
          const storageRef = ref(storage, `protocols/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          addImageToCanvas(url, ox, dropY);
          ox += 240;
        } catch (err) { console.error('Upload error:', err); }
      }
      setUploading(false);
    }
  };

  const handleFileInput = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let ox = 20;
    for (const file of files) {
      try {
        const storageRef = ref(storage, `protocols/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        addImageToCanvas(url, ox, 20);
        ox += 240;
      } catch (err) { console.error('Upload error:', err); }
    }
    setUploading(false);
    e.target.value = '';
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Toolbar */}
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: 1.5, textTransform: 'uppercase', fontFamily: 'var(--mono)' }}>
            Canvas {items.length > 0 && `· ${items.length}`}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={addNote} className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}><Type size={12} /> Note</button>
            {showSteps && <button onClick={addStep} className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}><List size={12} /> Step</button>}
            <button onClick={() => fileInputRef.current?.click()} className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}>
              <ImageIcon size={12} /> {uploading ? 'Uploading...' : 'Image'}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileInput} style={{ display: 'none' }} />
            <button onClick={() => audioInputRef.current?.click()} className="btn btn-ghost btn-sm" style={{ fontSize: 11 }}>
              <Music size={12} /> Audio
            </button>
            <input ref={audioInputRef} type="file" accept="audio/*" multiple onChange={handleAudioInput} style={{ display: 'none' }} />
          </div>
        </div>
      )}

      {/* Canvas wrapper with resize handles */}
      <div style={{ position: 'relative', width: canvasW, maxWidth: '100%' }}>
        <div
          ref={canvasRef}
          onClick={() => setSelected(null)}
          onDrop={handleDrop}
          onDragOver={e => { e.preventDefault(); if (!readOnly) setDraggingOver(true); }}
          onDragLeave={e => { if (!canvasRef.current?.contains(e.relatedTarget)) setDraggingOver(false); }}
          style={{
            position: 'relative',
            width: '100%',
            height: canvasH,
            background: '#07070f',
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,.07) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            border: `1px solid ${draggingOver ? 'var(--border-active)' : 'var(--border)'}`,
            borderRadius: 12,
            overflow: 'hidden',
            transition: 'border-color .15s',
          }}
        >
          {items.length === 0 && !readOnly && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'none' }}>
              <ImageIcon size={28} style={{ color: 'var(--dim)', opacity: .3 }} />
              <div style={{ fontSize: 12, color: 'var(--dim)', fontFamily: 'var(--mono)', textAlign: 'center', lineHeight: 1.9, opacity: .6 }}>
                Drag images from anywhere · add notes, steps, images<br />everything lives here
              </div>
            </div>
          )}

          {draggingOver && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(168,110,255,.05)', zIndex: 999, pointerEvents: 'none' }}>
              <div style={{ fontSize: 13, color: 'var(--purple2)', fontFamily: 'var(--mono)', fontWeight: 700, background: 'rgba(0,0,0,.8)', padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border-active)' }}>Drop to add</div>
            </div>
          )}

          {(items || []).map(item => (
            <CanvasItem
              key={item.id}
              item={item}
              selected={selected === item.id}
              onSelect={setSelected}
              onUpdate={updateItem}
              onDelete={deleteItem}
            />
          ))}
        </div>

        {/* Bottom resize handle */}
        <div
          onMouseDown={onBottomMouseDown}
          style={{ position: 'absolute', bottom: -4, left: 12, right: 12, height: 8, cursor: 'ns-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          <div style={{ width: 40, height: 3, borderRadius: 2, background: 'var(--border)', transition: 'background .15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--border-active)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--border)'} />
        </div>

        {/* Right resize handle */}
        <div
          onMouseDown={onRightMouseDown}
          style={{ position: 'absolute', top: 12, bottom: 12, right: -4, width: 8, cursor: 'ew-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
          <div style={{ width: 3, height: 40, borderRadius: 2, background: 'var(--border)', transition: 'background .15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--border-active)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--border)'} />
        </div>
      </div>
    </div>
  );
}
